import { BarChart as MuiBarChart, BarChartProps as MuiBarChartProps } from '@mui/x-charts';

export type BarChartProps = MuiBarChartProps & {
  // Chart data
  data: MuiBarChartProps['dataset'];
  // Series configuration
  series: MuiBarChartProps['series'];
  // xAxis configuration
  xAxis: MuiBarChartProps['xAxis'];
  // yAxis configuration
  yAxis: MuiBarChartProps['yAxis'];
  // Optional layout overrides
  height?: number;
  margin?: MuiBarChartProps['margin'];
  slotProps?: MuiBarChartProps['slotProps'];
  // Optional test id
  'data-testid'?: string;
  layout?: MuiBarChartProps['layout']; // 'vertical' or 'horizontal'
};

/**
 * Container-responsive bar chart.
 *
 * Width is intentionally omitted so MUI X Charts fills the parent's width
 * via its internal ResizeObserver — the chart now reflows when the
 * surrounding column is squeezed (e.g. when the Research Assistant
 * sidebar opens beside the cohort Results tab) instead of sizing off
 * `window.innerWidth`. Height stays fixed so the parent always resolves a
 * concrete vertical size (a responsive chart with neither dimension would
 * collapse to 0).
 */
export const BarChart = ({
  data,
  series,
  xAxis,
  yAxis,
  height = 450,
  margin = { left: 105, right: 50, top: 20, bottom: 70 },
  slotProps,
  'data-testid': dataTestId,
  layout = 'vertical',
  ...props
}: BarChartProps) => {
  if (!data || data.length === 0) return null;

  return (
    <MuiBarChart
      data-testid={dataTestId}
      height={height}
      margin={margin}
      dataset={data}
      series={series}
      slotProps={slotProps}
      xAxis={xAxis}
      yAxis={yAxis}
      layout={layout}
      {...props}
    />
  );
};
